import { useEffect, useState } from "react";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import { vi } from "date-fns/locale";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi";
import { isArchived } from "../models/OrgFunctions";
import Icon1 from "../icons/Icon";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken";
import sendInvitationCarnetEmail from "../api/send/sendInvitationCarnetEmail";
import removeCarnet from "../api/removeCarnet";
import Modal1 from "../modals/Modal1";
import WarningButton from "../buttons/WarningButton";
import { Spin } from "antd";
import { generateCarnetPdf } from "../api/generate/generatePdfSupport";

type CarnetAMProps = {
  carnetData: any; // Replace 'any' with the actual type if available
  prefix: string;
  handleClickVariant: (
    message: string,
    variant: "success" | "error" | "default" | "warning" | "info" | undefined
  ) => void;
  navigateWorkspace: (path: string) => void;
  orgData: any; // Replace 'any' with the actual type if available
  setActivationOpen: (open: boolean) => void;
};

export const CarnetAM = ({
  carnetData,
  prefix,
  handleClickVariant,
  navigateWorkspace,
  orgData,
  setActivationOpen,
}: CarnetAMProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [displayed, setDisplayed] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const handleDeleteCarnet = async () => {
    setLoadingDelete(true);
    setOpenDeleteModal(false);
    try {
      await removeCarnet(carnetData.id, prefix);
      handleClickVariant("Carnet supprimé", "success");
      navigateWorkspace("/carnets");
    } catch (error) {
      handleClickVariant("Erreur lors de la suppression du carnet", "error");
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleArchiveCarnet = async () => {
    let visions = carnetData.visions || [];
    let my_vision = visions.find((v: any) => {
      return v.org_id === orgData.id;
    });
    if (my_vision.state === "current") {
      my_vision.state = "archived";
      handleClickVariant("Carnet archivé", "success");
    } else {
      my_vision.state = "current";
      handleClickVariant("Carnet désarchivé", "success");
    }
    visions = visions.map((v: any) => {
      if (v.org_id === orgData.id) {
        return my_vision;
      }
      return v;
    });
    await updateDocDataFieldApi(carnetData.ref, "visions", visions, prefix);
    navigateWorkspace("/carnets");
  };

  const handleSendInvitationByEmail = async () => {
    const token = await getOrRefreshFirebaseToken();
    if (!carnetData?.email?.replace(/ /g, "")) {
      handleClickVariant("Le carnet n'a pas d'email renseigné", "error");
      return;
    }
    let res = await sendInvitationCarnetEmail(carnetData.id, token, prefix);
    if (res.success) {
      await updateDocDataFieldApi(carnetData.ref, "state", "sent", prefix);
      handleClickVariant("Invitation envoyée par email", "success");
    } else {
      handleClickVariant("Erreur lors de l'envoi de l'invitation", "error");
    }
    setOpen(false);
  };

  const handleGeneratePdf = async () => {
    setLoadingPdf(true);
    try {
      const result = await generateCarnetPdf(carnetData.id, prefix);
      if (result.success && result.pdf_url) {
        const response = await fetch(result.pdf_url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `carnet_${carnetData.code || carnetData.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(url);
        
        handleClickVariant("PDF généré avec succès", "success");
        if (result.synchronised){
          handleClickVariant("Le PDF a été envoyé au dossier Hexagone", "success");
        }
      } else {
        handleClickVariant("Erreur lors de la génération du PDF", "error");
      }
    } catch (error) {
      handleClickVariant("Erreur lors de la génération du PDF", "error");
    } finally {
      setLoadingPdf(false);
      setOpen(false);
    }
  };
  useEffect(() => {
    if (open) {
      setDisplayed(true);
    } else {
      const timer = setTimeout(() => {
        setDisplayed(false);
      }, 300); // Match the duration of the transition
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <div className="relative">
      <PrimaryButton
        onClick={() => {
          setOpen(!isVisible);
        }}
      >
        Actions
      </PrimaryButton>

      {open && (
        <>
          <div
            className="fixed h-screen w-screen top-0 left-0 z-10 bg-shadow_10 "
            onClick={() => setOpen(false)}
          ></div>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`
                ${displayed
                ? "translate-y-0 opacity-100"
                : "translate-y-4 opacity-0"
              }
               border
                absolute top-10 z-10 transition-all duration-300 ease-in-out 
              right-0 min-w-c200 text-h3 flex flex-col cursor-pointer h-fit  shadow-lg  text-black  rounded-xl`}
          >
            <div className="bg-wshadow_75 p-1 rounded-xl flex flex-col">
              <div className="flex flex-row gap-2 items-center ">
                <div className="flex flex-col  text-h3 w-fit">
                  {!carnetData.activated && (
                    <>
                      <ButtonAM
                        disabled={orgData.id !== carnetData.master_id}
                        onClick={() => {
                          if ((carnetData.phone?.replace(/ /g, "").length ?? 0) !== 10) {
                            handleClickVariant(
                              "Le numéro de téléphone de sécurité doit contenir 10 chiffres " +
                              carnetData.phone +
                              " (" +
                              (carnetData.phone?.length || 0) +
                              " chiffres)",
                              "error"
                            );
                            return;
                          }

                          setActivationOpen(true);
                          setIsVisible(false);
                          setOpen(false);
                        }}
                        icon_name="stars"
                        label="Activer le carnet"
                      ></ButtonAM>{" "}

                    </>
                  )}
                  <ButtonAM
                    disabled={!carnetData.activated}
                    onClick={() => {
                      handleSendInvitationByEmail();
                    }}
                    icon_name="send"
                    label="Envoyer l'invitation par email"
                  ></ButtonAM>{" "}
                  <ButtonAM
                    disabled={orgData.id !== carnetData.master_id}
                    onClick={() => {
                      setOpenDeleteModal(true);
                      setOpen(false);
                    }}
                    icon_name="delete"
                    label="Supprimer le carnet"
                  ></ButtonAM>
                  {carnetData.folder_id && (
                    <ButtonAM
                      onClick={() =>
                        window.open("https://web.hexagone.expert/folder/" + carnetData.folder_id)
                      }
                      icon_name="hexagone"
                      label="Voir le dossier (Hexagone)"
                    ></ButtonAM>
                  )}
                  <ButtonAM
                    onClick={handleArchiveCarnet}
                    icon_name="archive"
                    label={
                      isArchived(carnetData, orgData)
                        ? "Désarchiver"
                        : "Archiver" + " le carnet"
                    }
                  ></ButtonAM>
                  <ButtonAM
                    disabled={loadingPdf || !carnetData.activated}
                    onClick={handleGeneratePdf}
                    icon_name="document"
                    label={loadingPdf ? "Génération..." : "Générer PDF support"}
                  ></ButtonAM>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal1 isOpen={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <div className="flex flex-col bg-white rounded-xl p-4">
          <div className="text-h2">
            Voulez-vous vraiment supprimer ce carnet ?
          </div>
          <div className="text-h3 text-shadow_50 my-4">
            Cette action est <span className="text-red-500">irréversible</span>.
          </div>
          <div className="text-h3 text-shadow_50 mb-4">
            Elle entraîne la suppression définitive de ces éléments :
          </div>
          <div className="text-h3 text-shadow_50">- Tous les équipements du carnet</div>
          <div className="text-h3 text-shadow_50">- Toutes les tâches de maintenance</div>
          <div className="text-h3 text-shadow_50">- Tous les documents associés</div>
          <div className="text-h3 text-shadow_50 mb-4">- Le carnet lui-même</div>
          
          {!loadingDelete && (
            <div className="flex flex-row justify-end gap-2">
              <SecondaryButton onClick={() => setOpenDeleteModal(false)}>
                Annuler
              </SecondaryButton>
              <WarningButton disabled={false} onClick={handleDeleteCarnet} >
                Supprimer définitivement
              </WarningButton>
            </div>
          )}
          {loadingDelete && (
            <div className="flex justify-center">
              <Spin spinning={true} size="small" />
            </div>
          )}
        </div>
      </Modal1>
    </div>
  );
};

export const ButtonAM = ({
  onClick,
  icon_name,
  label,
  disabled,
}: {
  onClick?: () => void;
  icon_name: string;
  disabled?: boolean;
  label: string;
}) => {
  return (
    <div
      onClick={() => !disabled && onClick && onClick()}
      className={` 
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        flex flex-row gap-4 w-fit transition-all duration-300 ease-in-out min-w-c250 bg-white
                     rounded-lg py-3 px-4  hover:bg-neutral-100
                     `}
    >
      <Icon1 icon_name={icon_name} size={18}></Icon1>
      <span className="min-w-fit">{label}</span>
    </div>
  );
};
